import { Controller } from "@hotwired/stimulus"

// Réduction "3ème jeune" : la grille du QF inférieur ne s'ouvre que si trois
// jeunes de la famille s'inscrivent réellement, et elle profite aux tarifs les
// plus chers pour que la famille économise le plus.
//
// Chaque participant porte les deux listes de tarifs (plein / réduit) : on
// affiche celle qui lui revient et on désactive l'autre pour qu'elle ne soit pas
// soumise. Le serveur revalide de toute façon les tarifs reçus et le nombre de
// places de réduction consommées.
export default class extends Controller {
  static targets = ["participant"]
  static values = {
    threshold: { type: Number, default: 3 },
    // Frères et sœurs déjà inscrits à la même campagne par une autre souscription.
    alreadyEnrolled: { type: Number, default: 0 },
    prixes: Object
  }

  connect() {
    this.boundUpdate = (event) => this.handleChange(event)
    this.element.addEventListener("change", this.boundUpdate)
    // select2 réémet ses évènements jQuery en "$change" (voir le contrôleur
    // super-select de Bullet Train) : sans cela, un changement de tarif ne
    // redistribuerait pas les places de réduction.
    this.element.addEventListener("$change", this.boundUpdate)
    this.update()
  }

  disconnect() {
    this.element.removeEventListener("change", this.boundUpdate)
    this.element.removeEventListener("$change", this.boundUpdate)
  }

  handleChange(event) {
    // Ne réagir qu'aux décisions et aux tarifs, sinon les évènements que l'on
    // émet nous-mêmes sur le formulaire nous rappelleraient en boucle.
    const name = event.target?.name
    if (!name?.endsWith("[decision]") && !name?.endsWith("[prix_id]")) return
    this.update()
  }

  update() {
    const discounted = this.discountedParticipants()

    this.participantTargets.forEach((participant) => {
      this.applyDiscount(participant, discounted.includes(participant))
    })
  }

  // Les places ouvertes vont aux participants qui économisent le plus.
  discountedParticipants() {
    const enrolled = this.participantTargets.filter(
      (participant) => participant.dataset.jeune === "true" && this.decisionOf(participant) === "true"
    )

    const total = enrolled.length + this.alreadyEnrolledValue
    if (total < this.thresholdValue) return []

    const slots = Math.min(total - (this.thresholdValue - 1), enrolled.length)

    return enrolled
      .map((participant) => ({ participant, saving: this.savingFor(participant) }))
      .filter((entry) => entry.saving > 0)
      .sort((a, b) => b.saving - a.saving || this.byAge(a.participant, b.participant))
      .slice(0, slots)
      .map((entry) => entry.participant)
  }

  // Ce que la grille réduite ferait économiser à ce participant, d'après les
  // tarifs actuellement sélectionnés dans ses deux listes. `null` quand il n'a pas
  // de grille réduite (aucun tarif au QF inférieur pour sa catégorie).
  savingFor(participant) {
    const standard = this.selectIn(participant, "standard")
    const discounted = this.selectIn(participant, "discounted")
    if (!standard || !discounted) return null

    return this.amountOf(standard) - this.amountOf(discounted)
  }

  // Départage à économie égale : l'aîné d'abord, pour que l'affichage reste stable.
  byAge(a, b) {
    return (a.dataset.birthdate || "").localeCompare(b.dataset.birthdate || "")
  }

  amountOf(select) {
    return this.prixesValue[select.value] || 0
  }

  selectIn(participant, name) {
    return participant.querySelector(`[data-third-child-discount-target="${name}"] select`)
  }

  decisionOf(participant) {
    const checked = this.element.querySelector(
      `input[name="${participant.dataset.decisionName}"]:checked`
    )
    return checked?.value
  }

  applyDiscount(participant, discounted) {
    const standard = participant.querySelector('[data-third-child-discount-target="standard"]')
    const reduced = participant.querySelector('[data-third-child-discount-target="discounted"]')
    if (!standard || !reduced) return

    const standardChanged = this.toggleField(standard, !discounted)
    const reducedChanged = this.toggleField(reduced, discounted)
    if (standardChanged || reducedChanged) {
      // Le total affiché sur le bouton de paiement est recalculé par
      // subscription-form, qui écoute les évènements "change" du formulaire.
      this.element.dispatchEvent(new Event("change", { bubbles: false }))
    }
  }

  toggleField(wrapper, active) {
    const select = wrapper.querySelector("select")
    if (!select) return false

    const disabled = !active
    if (select.disabled === disabled) return false // déjà dans l'état voulu

    wrapper.classList.toggle("hidden", disabled)
    select.disabled = disabled
    return true
  }
}
